/**
 * CRA vocabulary shared by every page. Values mirror `backend/src/crabond/domain/enums.py`
 * exactly (they are persisted and returned verbatim by the API); labels are display text.
 */

/** CRA label vocabulary (backend `domain.enums.CraLabel` values = display strings). */
export const CRA_LABELS = [
  "CRA",
  "Likely CRA",
  'Unlikely but Possible "Extra Credit" CRA',
  "No",
  "Insufficient data",
  "Needs review",
  "Not evaluated",
] as const;

export type CraLabel = (typeof CRA_LABELS)[number];

export function isCraLabel(value: unknown): value is CraLabel {
  return typeof value === "string" && (CRA_LABELS as readonly string[]).includes(value);
}

/** Rank used to sort the results grid: strongest outcome first. */
export const LABEL_RANK: Record<CraLabel, number> = Object.fromEntries(
  CRA_LABELS.map((l, i) => [l, i]),
) as Record<CraLabel, number>;

export const DISCLAIMER = "Pre-purchase assessment — not a regulatory determination";

// ------------------------------------------------------------------------------- regimes
export const REGIME_IDS = ["CRA1995_QA2016", "OCC_FDIC_2026_NPRM"] as const;
export type RegimeId = (typeof REGIME_IDS)[number];

/** DESIGN §6 M5: every NPRM-profile output must carry this phrase. */
export const NPRM_NOTICE = "proposed rule — not in effect";

export const REGIME_LABELS: Record<RegimeId, { label: string; short: string; citation: string }> = {
  CRA1995_QA2016: {
    label: "1995 CRA rule (Appendix G) + 2016 Interagency Q&A",
    short: "1995 rule + 2016 Q&A",
    citation: "12 CFR 25/228/345 App. G",
  },
  OCC_FDIC_2026_NPRM: {
    label: "OCC/FDIC 2026 proposed rule (91 FR 52114)",
    short: "2026 NPRM",
    citation: "91 FR 52114 (Aug. 12, 2026)",
  },
};

export function isNprm(regime: string | null | undefined): boolean {
  return regime === "OCC_FDIC_2026_NPRM";
}

// -------------------------------------------------------------------------------- statuses
export const DETERMINATION_STATUSES = [
  "QUALIFIES",
  "QUALIFIES_INTENT",
  "LIKELY",
  "PRO_RATA",
  "CONDITIONAL_RESPONSIVENESS",
  "NEEDS_DATA",
  "NEEDS_REVIEW",
  "NOT_QUALIFIED",
  "NOT_EVALUATED",
] as const;
export type DeterminationStatus = (typeof DETERMINATION_STATUSES)[number];

const LABEL_FOR_STATUS: Record<DeterminationStatus, CraLabel> = {
  QUALIFIES: "CRA",
  QUALIFIES_INTENT: "Likely CRA",
  LIKELY: "Likely CRA",
  PRO_RATA: "Likely CRA",
  CONDITIONAL_RESPONSIVENESS: 'Unlikely but Possible "Extra Credit" CRA',
  NEEDS_DATA: "Insufficient data",
  NEEDS_REVIEW: "Needs review",
  NOT_QUALIFIED: "No",
  NOT_EVALUATED: "Not evaluated",
};

/** Mirror of backend `label_for(status)` (DESIGN §4.1), for rows that only carry a status. */
export function labelForStatus(status: string | null | undefined): CraLabel {
  return LABEL_FOR_STATUS[status as DeterminationStatus] ?? "Not evaluated";
}

export const STATUS_LABELS: Record<DeterminationStatus, string> = {
  QUALIFIES: "Qualifies",
  QUALIFIES_INTENT: "Qualifies (bona fide intent)",
  LIKELY: "Likely",
  PRO_RATA: "Pro rata",
  CONDITIONAL_RESPONSIVENESS: "Conditional on responsiveness",
  NEEDS_DATA: "Needs data",
  NEEDS_REVIEW: "Needs review",
  NOT_QUALIFIED: "Not qualified",
  NOT_EVALUATED: "Not evaluated",
};

export const CRITERION_STATUSES = [
  "PASS",
  "FAIL",
  "REVIEW",
  "INSUFFICIENT_DATA",
  "NOT_APPLICABLE",
] as const;
export type CriterionStatus = (typeof CRITERION_STATUSES)[number];

export const CRITERION_LABELS: Record<CriterionStatus, string> = {
  PASS: "Pass",
  FAIL: "Fail",
  REVIEW: "Review",
  INSUFFICIENT_DATA: "Insufficient data",
  NOT_APPLICABLE: "N/A",
};

// ---------------------------------------------------------------------------------- prongs
export const PRONGS = [
  "AFFORDABLE_HOUSING",
  "COMMUNITY_SERVICES",
  "ECONOMIC_DEVELOPMENT",
  "REVITALIZE_LMI",
  "REVITALIZE_DISASTER",
  "REVITALIZE_DISTRESSED",
  "REVITALIZE_UNDERSERVED",
] as const;
export type Prong = (typeof PRONGS)[number];

export const PRONG_LABELS: Record<Prong, string> = {
  AFFORDABLE_HOUSING: "Affordable housing",
  COMMUNITY_SERVICES: "Community services",
  ECONOMIC_DEVELOPMENT: "Economic development",
  REVITALIZE_LMI: "Revitalize/stabilize — LMI",
  REVITALIZE_DISASTER: "Revitalize/stabilize — disaster",
  REVITALIZE_DISTRESSED: "Revitalize/stabilize — distressed",
  REVITALIZE_UNDERSERVED: "Revitalize/stabilize — underserved",
};

/** The NPRM renames community services to "civic assistance" (DESIGN §6 M5). */
export function prongLabel(prong: string | null | undefined, regime?: string | null): string {
  if (!prong) return "—";
  if (prong === "COMMUNITY_SERVICES" && isNprm(regime)) return "Civic assistance";
  return PRONG_LABELS[prong as Prong] ?? prong;
}

// ----------------------------------------------------------------------------- asset class
export const ASSET_CLASS_LABELS: Record<string, string> = {
  MUNI: "Municipal",
  AGENCY_MF: "Agency multifamily",
  AGENCY_SF_MBS: "Agency SF MBS",
  SBA: "SBA",
  TREASURY_AGENCY_DEBT: "Treasury/agency",
  CORPORATE: "Corporate",
  OTHER: "Other",
  UNKNOWN: "Unknown",
};

export function assetClassLabel(code: string | null | undefined): string {
  if (!code) return "—";
  return ASSET_CLASS_LABELS[code] ?? code;
}

export const AA_BENEFIT_LABELS: Record<string, string> = {
  AA_DIRECT: "Directly benefits the AA",
  AA_BROADER_INCLUDES_AA: "Broader statewide/regional area that includes the AA",
  BROADER_NOT_SERVING_AA: "Broader area not serving the AA",
  OUTSIDE: "Outside the bank's AAs",
  UNKNOWN_NO_BANK_CONTEXT: "Bank-agnostic (no bank selected)",
};

export const EVIDENCE_LEVEL_LABELS: Record<string, string> = {
  REGULATORY_AGREEMENT: "Regulatory agreement",
  GOVERNMENT_PROGRAM: "Government program",
  RESTRICTED_AMI_BANDS: "Restricted AMI bands",
  RENT_ROLL_AMI_BANDS: "Rent-roll AMI bands",
  RENT_TEST_COMPUTED: "Computed rent test",
  NONE: "None",
};

// --------------------------------------------------------------------------- provenance
export const SOURCE_KINDS = [
  "USER_OVERRIDE",
  "OFFICIAL_STATEMENT",
  "STATE_DEBT_DB",
  "USER_CSV",
  "OPENFIGI",
  "AGENCY_DISCLOSURE",
  "HUD",
  "HEURISTIC",
  "COMPUTED",
] as const;
export type SourceKind = (typeof SOURCE_KINDS)[number];

export const SOURCE_KIND_META: Record<SourceKind, { short: string; label: string }> = {
  USER_OVERRIDE: { short: "Override", label: "Analyst override" },
  OFFICIAL_STATEMENT: { short: "OS", label: "Official statement" },
  STATE_DEBT_DB: { short: "State DB", label: "State debt database" },
  USER_CSV: { short: "CSV", label: "Uploaded vendor file" },
  OPENFIGI: { short: "OpenFIGI", label: "OpenFIGI" },
  AGENCY_DISCLOSURE: { short: "Agency", label: "Agency disclosure" },
  HUD: { short: "HUD", label: "HUD data" },
  HEURISTIC: { short: "Heuristic", label: "Heuristic (lowest precedence)" },
  COMPUTED: { short: "Computed", label: "Computed by the platform" },
};

/** EMMA's terms forbid automated access; the UI offers a deep link and an upload instead. */
export function emmaUrl(cusip: string): string {
  return `https://emma.msrb.org/QuickSearch/Results?quickSearchText=${encodeURIComponent(cusip)}`;
}

export const ROLES = ["platform_admin", "tenant_admin", "analyst", "viewer"] as const;
export type RoleName = (typeof ROLES)[number];

export const ROLE_LABELS: Record<RoleName, string> = {
  platform_admin: "Platform admin",
  tenant_admin: "Tenant admin",
  analyst: "Analyst",
  viewer: "Viewer",
};

/** Analysts and above may mutate (mirrors backend `require_write`). */
export function canWrite(role: string | null | undefined): boolean {
  return role === "platform_admin" || role === "tenant_admin" || role === "analyst";
}
